import { lazy, Suspense, useEffect, useState, useTransition } from 'react';
import { createRoot } from 'react-dom/client';
// Above the page imports on purpose: ES modules evaluate in import order, and
// the globals here must land in the stylesheet before any page's rules.
import './shared/styles/base.css';
import Landing from './pages/landing/Landing.jsx';
import { LEGACY_PATHS, ROUTE_PATHS, normalizePath } from './routes.js';
import { onRouteChange, routeForHref, startRouter } from './shared/router.js';

// `/` is imported eagerly: it is the page most visitors land on, and a lazy
// chunk would cost it an extra round trip for no gain. The other three are
// lazy, so their JS and CSS only reach someone who actually opens them — but
// they are also prefetched once the landing page is idle (see warmRoute), so
// "only on demand" does not turn into "wait for a download after the click".
const LOADERS = {
  '/privacy-policy': () => import('./pages/privacy-policy/PrivacyPolicy.jsx'),
  '/docs': () => import('./pages/docs/Docs.jsx'),
  '/support': () => import('./pages/support/Support.jsx'),
};

const PAGES = {
  '/': Landing,
  ...Object.fromEntries(Object.entries(LOADERS).map(([path, load]) => [path, lazy(load)])),
};

/**
 * Prefetch the routes the visitor has not asked for yet.
 *
 * Without this the first click on Document or Support paid for a round trip
 * mid-navigation: the old page unmounted, `Suspense` had nothing to show, and
 * the screen sat blank for the length of the fetch — a quarter of a second on a
 * throttled connection, and visible as a jolt even on a fast one.
 *
 * Repeat calls are free: a dynamic import resolves from the module registry
 * after the first one, so this never fetches a chunk twice.
 */
function warmRoute(path) {
  return LOADERS[path]?.();
}

function prefetchRoutes() {
  const whenIdle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));

  // Idle time only, so the landing page's own load is untouched — and one route
  // at a time, most-visited first. Firing all three at once made them compete
  // with the chunk of a route clicked while they were still in flight, which is
  // the one request that must not be slowed down.
  const queue = ['/docs', '/support', '/privacy-policy'];
  const warmNext = () => {
    const path = queue.shift();
    if (path) warmRoute(path)?.finally(() => whenIdle(warmNext, { timeout: 2500 }));
  };
  whenIdle(warmNext, { timeout: 2500 });

  // For the visitor who clicks before the browser ever goes idle: pointerover
  // fires ~200ms before the click on a mouse, and focusin covers the keyboard,
  // where the tab stop lands well before Enter.
  const hint = (event) => {
    const anchor = event.target.closest?.('a[href]');
    const route = anchor && routeForHref(anchor.href);
    if (route) warmRoute(route);
  };
  document.addEventListener('pointerover', hint, { passive: true });
  document.addEventListener('focusin', hint, { passive: true });
}

function App() {
  // The path is state, not a one-time read: shared/router.js intercepts clicks
  // on our own links and pushes history instead of reloading the document.
  const [path, setPath] = useState(() => normalizePath(window.location.pathname));
  const [, startTransition] = useTransition();

  // A transition, so that a route whose chunk has not arrived yet keeps the
  // page the visitor is looking at on screen instead of unmounting it and
  // leaving the Suspense fallback — a blank screen — in its place. Prefetching
  // makes that window ~0 in the common case; this covers the rest.
  useEffect(
    () => onRouteChange((next) => startTransition(() => setPath(next))),
    [],
  );

  const Page = PAGES[path] || Landing;
  return (
    <Suspense fallback={null}>
      <Page />
    </Suspense>
  );
}

// Both servers redirect a legacy path before the bundle ever loads; this only
// catches an index.html already in someone's cache, which would otherwise land
// them on the landing page with the old URL still in the bar.
const legacy = LEGACY_PATHS[normalizePath(window.location.pathname)];
if (legacy) {
  window.location.replace(legacy + window.location.hash);
} else {
  startRouter();
  createRoot(document.getElementById('root')).render(<App />);
  prefetchRoutes();
}
